/**
 * Elevation scale (Figma: Liquid Glass, node 89519-22559).
 *
 * Two stacked drop shadows per step over the ink base #131927. Merged into
 * `theme.palette.shadows` alongside the existing `lightBlack` / `darkBlack`
 * entries, which predate this scale and are still referenced by
 * componentOverrides and several pages.
 */

/** Base ink the shadows are tinted with: rgb(19, 25, 39). */
export const SHADOW_INK = '19, 25, 39';

const ink = (alpha: number) => `rgba(${SHADOW_INK}, ${alpha})`;

export const SHADOW_100 = `0px 2px 4px -2px ${ink(
  0.12
)}, 0px 4px 4px -2px ${ink(0.08)}`;
export const SHADOW_200 = `0px 4px 6px -4px ${ink(
  0.12
)}, 0px 8px 8px -4px ${ink(0.08)}`;
export const SHADOW_300 = `0px 6px 8px -6px ${ink(
  0.12
)}, 0px 8px 16px -6px ${ink(0.08)}`;
export const SHADOW_400 = `0px 6px 12px -6px ${ink(
  0.12
)}, 0px 8px 24px -4px ${ink(0.08)}`;
export const SHADOW_500 = `0px 6px 14px -6px ${ink(
  0.12
)}, 0px 10px 32px -4px ${ink(0.1)}`;
export const SHADOW_600 = `0px 8px 18px -6px ${ink(
  0.12
)}, 0px 12px 42px -4px ${ink(0.12)}`;
export const SHADOW_700 = `0px 8px 22px -6px ${ink(
  0.12
)}, 0px 14px 64px -4px ${ink(0.12)}`;
export const SHADOW_800 = `0px 8px 28px -6px ${ink(
  0.12
)}, 0px 18px 88px -4px ${ink(0.14)}`;

/**
 * Named surface shadows from the Glass screens (Figma: Design page). These sit
 * alongside the numeric scale because the design applies them by surface role
 * rather than by elevation step.
 */

/** Glass surfaces: toggle tracks and items, tier cards, badges. */
export const SHADOW_GLASS = '0 4px 12px rgba(0, 0, 0, 0.06)';
/** Content cards: "Plan Benefits Card", "Camera View", input field. */
export const SHADOW_CARD = '0 4px 12px rgba(0, 0, 0, 0.1)';
/** Section sheets: the "Content Area" cards and large modals. */
export const SHADOW_SHEET = '0 0 32px rgba(0, 0, 0, 0.2)';
/**
 * The solid disc behind an icon (Figma "Round Button"). Offset on both axes
 * rather than straight down, which is why it is not a step on the scale.
 *
 * Light mode dropped a touch further (0.08 -> 0.05) — reported live as
 * slightly heavy on a pale card. Dark mode wasn't flagged, so kept as-is.
 */
export const SHADOW_ICON = '2.26px 2.26px 11.3px -4.52px rgba(0, 0, 0, 0.08)';
export const SHADOW_ICON_LIGHT =
  '2.26px 2.26px 11.3px -4.52px rgba(0, 0, 0, 0.05)';
/** A raised control knob sitting on its track — the toggle thumb. */
export const SHADOW_THUMB = '0 2px 4px rgba(0, 0, 0, 0.12)';

/**
 * The two pre-elevation-scale shadows this file's own header comment already
 * anticipated moving here. Used to be authored per-partner in a `surfaces.ts`
 * `bespoke.shadows` field — checked every partner's actual value and they
 * were byte-identical everywhere (`rgba(0,0,0,0.21)` / `rgba(0,0,0,0.25)`,
 * some partners just spelled it as `#00000036`/`#00000040` — same numbers),
 * never genuinely partner-specific, so there's no reason for a bespoke
 * slot: one constant pair, not six duplicated files. Plain black, not the
 * `ink` tint the rest of this scale uses — these predate that convention.
 */
export const SHADOW_LEGACY_LIGHT = '0px 4px 4px 0px rgba(0, 0, 0, 0.21)';
export const SHADOW_LEGACY_DARK = '0px 4px 4px 0px rgba(0, 0, 0, 0.25)';

export const elevation = {
  glass: SHADOW_GLASS,
  card: SHADOW_CARD,
  sheet: SHADOW_SHEET,
  icon: SHADOW_ICON,
  thumb: SHADOW_THUMB,
  lightBlack: SHADOW_LEGACY_LIGHT,
  darkBlack: SHADOW_LEGACY_DARK,
  100: SHADOW_100,
  200: SHADOW_200,
  300: SHADOW_300,
  400: SHADOW_400,
  500: SHADOW_500,
  600: SHADOW_600,
  700: SHADOW_700,
  800: SHADOW_800
};
